import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { FerringWordmark } from './FerringWordmark';
import { IdeaPanel } from './IdeaPanel';
import { ExamplePanel } from './ExamplePanel';
import { FormatPanel } from './FormatPanel';
import { ExtensionsPanel } from './ExtensionsPanel';
type ConceptModalProps = {
  open: boolean;
  onClose: () => void;
};
type TabId = 'idea' | 'example' | 'format' | 'extensions';
const TABS: {
  id: TabId;
  label: string;
}[] = [{
  id: 'idea',
  label: 'The Snakker idea'
}, {
  id: 'example',
  label: 'This example'
}, {
  id: 'format',
  label: 'Format & insight'
}, {
  id: 'extensions',
  label: 'Extensions & use'
}];
const EASE = [0.23, 1, 0.32, 1] as const;
export function ConceptModal({
  open,
  onClose
}: ConceptModalProps) {
  const [tab, setTab] = useState<TabId>('idea');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-8" data-mp="2yak2gmps5mpm">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-9" data-mp="2y9idrffud1pf">
          <button type="button" aria-label="Close concept" onClick={onClose} className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" data-id="element-10" data-mp="2tdbkam4mtiod" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="concept-title" className="relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_40px_100px_-30px_rgba(14,27,51,0.5)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} data-id="element-11" data-mp="2ff3gbu96hh3p">
            <header className="flex items-center justify-between gap-4 border-b border-navy/10 px-6 py-4 md:px-10" data-id="element-12" data-mp="18uhfp2ua2m3w">
              <div className="flex items-center gap-5" data-id="element-13" data-mp="14scpyf6ya9mj">
                <FerringWordmark data-id="element-14" data-mp="14tnvokjyhrga" />
                <span className="hidden h-7 w-px bg-navy/10 sm:block" aria-hidden="true" data-id="element-15" data-mp="ztjcgmrnyimd" />
                <p id="concept-title" className="hidden font-mono text-[11px] tracking-[0.24em] text-navy/50 sm:block" data-id="element-16" data-mp="uuotnktp4u29">
                  FERRING × SNAKKER™ · CONCEPT PROPOSAL
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2 text-navy/60 transition-colors duration-150 hover:bg-paper hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-ferring" data-id="element-17" data-mp="3fke8vyybahvh">
                <XIcon size={20} strokeWidth={1.75} data-id="element-18" data-mp="3fd3mulktz5vl" />
              </button>
            </header>

            <nav className="flex gap-1 overflow-x-auto border-b border-navy/10 px-4 md:px-8" role="tablist" aria-label="Concept sections" data-id="element-19" data-mp="3k7zomdfvd7lw">
              {TABS.map(t => {
            const active = t.id === tab;
            return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(t.id)} className={`relative whitespace-nowrap px-3 py-3.5 text-sm transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ferring ${active ? 'text-navy' : 'text-navy/50 hover:text-navy'}`} data-id="element-20" data-mp="200i2ueujosa">
                    {t.label}
                    {active && <motion.span layoutId="concept-tab" className="absolute inset-x-3 bottom-0 h-[2px] bg-ferring" transition={{
                duration: 0.2,
                ease: EASE
              }} data-id="element-21" data-mp="c35i5how6ria" />}
                  </button>;
          })}
            </nav>

            <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10" role="tabpanel" data-id="element-22" data-mp="3fkw39i4p95uf">
              <AnimatePresence mode="wait" initial={false} data-id="element-23" data-mp="3bj27no9d2s14">
                <motion.div key={tab} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} data-id="element-24" data-mp="3p9npraqzqxxy">
                  {tab === 'idea' && <IdeaPanel data-id="element-25" data-mp="2wr0aibyoed4s" />}
                  {tab === 'example' && <ExamplePanel data-id="element-26" data-mp="2rtia59yq568e" />}
                  {tab === 'format' && <FormatPanel data-id="element-27" data-mp="17c90viiidwmu" />}
                  {tab === 'extensions' && <ExtensionsPanel data-id="element-28" data-mp="17ao5xrsa8bog" />}
                </motion.div>
              </AnimatePresence>
            </div>

            <footer className="border-t border-navy/10 bg-paper px-6 py-5 md:px-10" data-id="element-29" data-mp="1c52aajox9t59">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between" data-id="element-30" data-mp="yddxee6un0n8">
                <div data-id="element-31" data-mp="13b5r9dr20wu2">
                  <p className="text-base text-navy/50" data-id="element-32" data-mp="1389ptffzzq0v">The opportunity is not this exact teaser.</p>
                  <p className="text-lg font-medium text-navy" data-id="element-33" data-mp="1qydulq4x9vtg">The opportunity is a different way of communicating product value.</p>
                </div>
                <p className="font-mono text-[11px] tracking-[0.22em] text-ferring" data-id="element-34" data-mp="1h194jf7yhg7d">SHORT. INTERACTIVE. REWARDING. MEASURABLE.</p>
              </div>
              <p className="mt-4 text-[11px] leading-relaxed text-navy/45" data-id="element-35" data-mp="5mvkklgbpkwm">
                This is an illustrative Snakker prototype created to demonstrate the format and interaction potential. The clinical
                story, creative execution and final campaign would be developed together with Ferring. Clinical content and
                comparative claims subject to Ferring medical/legal review. Concept &amp; creative direction © Snakker™ 2026 ·
                Confidential &amp; proprietary.
              </p>
            </footer>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}